import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import { useStateSelector } from "utils/field";
import { getOptions, getBaseUnits, getUnitOptions, getFirstOption } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getUdefWindowCodeEmbodiedCarbonDisplay } from "utils/enclosure/embc/udefWindowLayers";
import {
    getWindowOverallUValueMetric,
    predictFrameMaterialFromLogisticModel,
    UDEF_GLAZING_TYPE_ID_TO_LAYERS,
    UDEF_WINDOW_STYLE_ID_TO_CONSOLIDATED,
} from "utils/enclosure/windows";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Field from "components/Field";
import Accordion from "components/Accordion";
import AccordionActions from "../../AccordionActions";
import { GradeDisplay, PercentageDisplay } from "components/EmbodiedCarbonGrade";
import EmbodiedCarbonEditor from "components/EmbodiedCarbonGrade/UdefEmbodiedCarbonEditor";

import classes from "features/Model/Enclosure/UserDefinedCodes/style.module.scss";

const overallResistanceValidation = getValidation("udefWindowOverallThermalResistance");
const overallTransmittanceValidation = getValidation("udefWindowOverallThermalResistanceUValue");
const frameHeightValidation = getValidation("udefWindowOverallFrameHeight");
const shgcValidation = getValidation("udefWindowOverallSHGC");
const glazingValidation = getValidation("udefWindowOverallGlazingType");
const styleValidation = getValidation("udefWindowOverallStyle");
const fillGasValidation = getValidation("udefWindowOverallFillGas");
const lowECoatingValidation = getValidation("udefWindowOverallLowECoating");
const frameMaterialValidation = getValidation("csWindowFrameMaterial");

const WindowOverall = React.memo(({ id, change, accessor, drawerAccessor, barMetric = "percentage" }) => {
    const dispatch = useDispatch();
    const overallThermalResistance = useStateSelector(`${accessor}.overallThermalResistance`) || {};
    const { id: overallResistanceId, value: overallResistanceValue } = overallThermalResistance;
    const overallLayers = useStateSelector(`${drawerAccessor}.uDefCode.layers.window`);
    const { id: glazingTypeId = 0 } = useStateSelector(`${accessor}.glazingType`) || {};
    const { id: windowStyleId = 0 } = useStateSelector(`${accessor}.windowStyle`) || {};
    const shgc = useStateSelector(`${accessor}.shgc`);
    const frameMaterial = useStateSelector(`${accessor}.frameMaterial`) || {};

    const overallResistanceUnits = useStateSelector(`${accessor}.overallThermalResistance_u`);
    const frameHeightUnits = useStateSelector(`${accessor}.frameHeight_u`);
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};

    const frameMaterialTouchedByUser = useRef(false);
    const previousOverallLayerIdRef = useRef();
    useEffect(() => {
        if (previousOverallLayerIdRef.current !== undefined && previousOverallLayerIdRef.current !== id) {
            frameMaterialTouchedByUser.current = false;
        }
        previousOverallLayerIdRef.current = id;
    }, [id]);

    const frameOpts = useMemo(
        () => getOptions({ fieldKey: "csWindowFrameMaterial", indKey: glazingTypeId }),
        [glazingTypeId]
    );

    useEffect(() => {
        // Reset frame material if option is not in the frame options list.
        const optionMatches = frameOpts.filter(
            (opt) => opt.value.value === frameMaterial.value && opt.value.id === frameMaterial.id
        );

        if (isEmpty(optionMatches)) {
            change(
                `${accessor}.frameMaterial`,
                getFirstOption({ indKey: glazingTypeId, depKey: "csWindowFrameMaterial" })
            );
        }
    }, [frameMaterial, glazingTypeId, frameOpts, change]);

    const applyEstimatedFrameMaterial = useCallback(() => {
        if (frameMaterialTouchedByUser.current) return;

        const uMetric = getWindowOverallUValueMetric(
            { id: overallResistanceId, value: overallResistanceValue },
            overallResistanceUnits
        );
        if (uMetric == null) return;
        if (shgc == null || !Number.isFinite(Number(shgc))) return;

        const layers = UDEF_GLAZING_TYPE_ID_TO_LAYERS[glazingTypeId] ?? 2;
        const styleName = UDEF_WINDOW_STYLE_ID_TO_CONSOLIDATED[windowStyleId] ?? "Casement";

        const est = predictFrameMaterialFromLogisticModel({
            uMetric,
            shgc: Number(shgc),
            layersOfGlazing: Number(layers),
            styleConsolidated: styleName,
        });
        if (est == null) return;
        if (String(frameMaterial?.value) === String(est)) return;

        const opt = frameOpts.find((o) => o.value && String(o.value.value) === String(est));
        if (!opt) return;

        change(`${accessor}.frameMaterial`, { id: opt.value.id, value: String(est) });
    }, [
        accessor,
        change,
        frameMaterial?.value,
        frameOpts,
        overallResistanceId,
        overallResistanceValue,
        overallResistanceUnits,
        glazingTypeId,
        shgc,
        windowStyleId,
    ]);

    useEffect(() => {
        applyEstimatedFrameMaterial();
    }, [applyEstimatedFrameMaterial]);

    const onFrameMaterialChange = useCallback(() => {
        frameMaterialTouchedByUser.current = true;
    }, []);

    const deleteLayer = useCallback(() => {
        const { [id]: _, ...remainingLayers } = overallLayers;
        change(`${drawerAccessor}.uDefCode.layers.window`, remainingLayers);
        dispatch(removeFormErrors(accessor));
    }, [id, overallLayers, change, dispatch, drawerAccessor, accessor]);

    const layer =
        Object.keys(overallLayers).map((key) => ({
            ...overallLayers[key],
            id: key,
            type: "windowOverall",
        }))[0] || {};

    const display = useMemo(() => {
        return getUdefWindowCodeEmbodiedCarbonDisplay({ layers: layer });
    }, [overallLayers]);

    const showGrade = barMetric === "grade";

    const gradeInner = (
        <GradeDisplay
            emissions={display.emissions}
            storage={display.storage}
            emissionGrade={display.emissionGrade}
            storageGrade={display.storageGrade}
            withBorder={true}
            borderHeight={"100%"}
            borderColor={"#E1EAF0"}
            customGap={"0.5rem"}
            customPadding={"0 7px"}
            hasMismatch={display.hasMismatch}
        />
    );

    const percentageInner = (
        <PercentageDisplay
            emissions={display.emissions}
            storage={display.storage}
            withBorder={true}
            borderHeight={"100%"}
            borderColor={"#E1EAF0"}
            customGap={"0.5rem"}
            customPadding={"0 7px"}
            hasMismatch={display.hasMismatch}
        />
    );

    return (
        <div>
            <Accordion
                className={classes.accordion}
                initOpenState={true}
                heading={
                    <span className={classes.accordionHeader}>
                        <span className={classes.accordionTitle}>Overall Characteristics</span>
                    </span>
                }
                hoverActions={<AccordionActions accessor={accessor} deleteLayer={deleteLayer} />}
                bodyClassName={classes.accordionBodyContent}
                carbonGrade={
                    <span className={classes.gradeContainer}>{showGrade ? gradeInner : percentageInner}</span>
                }
            >
                <div className={classes.inputFields}>
                    <InputRow gridTemplate="1fr 1fr">
                        <Field
                            component={Select}
                            name={`${accessor}.overallThermalResistance`}
                            options={[
                                {
                                    label: "RSI/R-Value",
                                    value: {
                                        id: 0,
                                        value:
                                            overallResistanceId === 1
                                                ? 1 / overallResistanceValue
                                                : overallResistanceValue,
                                    },
                                },
                                {
                                    label: "U-Value",
                                    value: {
                                        id: 1,
                                        value:
                                            overallResistanceId === 0
                                                ? 1 / overallResistanceValue
                                                : overallResistanceValue,
                                    },
                                },
                            ]}
                            label="Overall Thermal Resistance"
                            placeholder="Choose Input Type"
                            validate={overallResistanceValidation}
                            info={"Select input units for overall thermal resistance."}
                        />
                        {overallResistanceId === 0 && (
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.overallThermalResistance.value`}
                                label="Thermal Resistance Value"
                                placeholder={0.0}
                                validate={overallResistanceValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefWindowOverallThermalResistance")}
                                change={change}
                                setValue={overallResistanceValue}
                                units={{
                                    base: getBaseUnits("udefWindowOverallThermalResistance", modelUnits),
                                    options: getUnitOptions("thermalResistance"),
                                    selected: overallResistanceUnits,
                                    unitType: "thermalResistance",
                                    accessor: `${accessor}.overallThermalResistance_u`,
                                }}
                            />
                        )}
                        {overallResistanceId === 1 && (
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.overallThermalResistance.value`}
                                label="Thermal Transmittance Value"
                                placeholder={0.0}
                                validate={overallTransmittanceValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefWindowOverallThermalResistanceUValue")}
                                change={change}
                                setValue={overallResistanceValue}
                                units={{
                                    base: getBaseUnits("udefWindowOverallThermalResistanceUValue", modelUnits),
                                    options: ["W/m²K"],
                                    selected: "W/m²K",
                                    unitType: "thermalTramsmittance",
                                    accessor: `${accessor}.overallThermalResistance_u`,
                                }}
                            />
                        )}
                    </InputRow>
                    <InputRow gridTemplate="1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.frameHeight`}
                            label="Frame Height"
                            placeholder={0.0}
                            validate={frameHeightValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("udefWindowOverallFrameHeight")}
                            change={change}
                            units={{
                                base: getBaseUnits("udefWindowOverallFrameHeight", modelUnits),
                                options: getUnitOptions("length"),
                                selected: frameHeightUnits,
                                unitType: "length",
                                accessor: `${accessor}.frameHeight_u`,
                            }}
                        />
                        <Field
                            component={Input}
                            type="number"
                            name={`${accessor}.shgc`}
                            label="SHGC"
                            placeholder={0.0}
                            validate={shgcValidation}
                            decimals={getDecimalPlaces("udefWindowOverallSHGC")}
                        />
                    </InputRow>
                    <InputRow gridTemplate="1fr 1fr">
                        <Field
                            component={Select}
                            name={`${accessor}.glazingType`}
                            options={getOptions({ fieldKey: "udefWindowOverallGlazingType" })}
                            label="Glazing Type"
                            placeholder="Choose Glazing Type"
                            validate={glazingValidation}
                        />
                        <Field
                            component={Select}
                            name={`${accessor}.windowStyle`}
                            options={getOptions({ fieldKey: "udefWindowOverallStyle" })}
                            label="Window Type"
                            placeholder="Choose Window Type"
                            validate={styleValidation}
                        />
                    </InputRow>
                    <InputRow gridTemplate="1fr 1fr">
                        <Field
                            component={Select}
                            name={`${accessor}.lowECoating`}
                            options={getOptions({ fieldKey: "udefWindowOverallLowECoating" })}
                            label="Low-E Coating"
                            placeholder="Choose Low-E Coating Type"
                            validate={lowECoatingValidation}
                        />
                        <Field
                            component={Select}
                            name={`${accessor}.fillGas`}
                            options={getOptions({ fieldKey: "udefWindowOverallFillGas" })}
                            label="Fill Gas"
                            placeholder="Choose Fill Gas"
                            validate={fillGasValidation}
                        />
                    </InputRow>
                    <InputRow gridTemplate="1fr 1fr">
                        <Field
                            component={Select}
                            name={`${accessor}.frameMaterial`}
                            label="Frame Material"
                            placeholder="Choose Frame Material"
                            options={frameOpts}
                            validate={frameMaterialValidation}
                            onChange={onFrameMaterialChange}
                            info="This value is not used by HOT2000. If unedited, frame material will be estimated based on the U-value, SHGC, and window type based on a logistic regression fit model derived from the ENERGY STAR list of most efficient windows. This value is used to estimate the embodied carbon of the window, which highly correlates with the number of glazing layers and frame material."
                        />
                    </InputRow>
                </div>
                <div className={classes.embodiedCarbonEditor}>
                    <EmbodiedCarbonEditor
                        change={change}
                        display={display}
                        layersFieldPrefix={accessor}
                        layerType={"windowOverall"}
                        layer={layer}
                    />
                </div>
            </Accordion>
        </div>
    );
});

export default WindowOverall;
